import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight, Globe, Check } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useLang } from "@/i18n/LanguageContext";

const LINK_KEYS = [
  { key: "about", href: "#about" },
  { key: "products", href: "#products" },
  { key: "markets", href: "#markets" },
  { key: "founder", href: "#founder" },
  { key: "contact", href: "#contact" },
];

export const Navbar = () => {
  const { t, lang, setLang, langs } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const LangSwitcher = ({ testid }) => (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-testid={testid}
        className="inline-flex items-center gap-2 text-vw-ink2 hover:text-vw-green transition-colors duration-300 font-mono text-xs uppercase tracking-wider outline-none"
      >
        <Globe size={16} />
        {langs.find((l) => l.code === lang)?.label}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-white border-vw-silver rounded-none min-w-[160px]">
        {langs.map((l) => (
          <DropdownMenuItem
            key={l.code}
            data-testid={`lang-${l.code}`}
            onClick={() => setLang(l.code)}
            className="cursor-pointer font-sans text-sm flex items-center justify-between focus:bg-vw-bg3 rounded-none"
          >
            {l.label}
            {lang === l.code && <Check size={14} className="text-vw-green" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <motion.header
      data-testid="navbar"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-vw-silver" : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="mx-auto max-w-[1440px] px-6 md:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="logo" className="flex items-center gap-3 group">
          <span className="inline-flex items-center justify-center h-11 w-11 bg-vw-green text-white font-serif text-2xl leading-none tracking-tight">
            VW
          </span>
          <span className="hidden xl:block font-serif text-2xl tracking-tight text-vw-ink">
            Vardanta <span className="italic font-light">World</span>
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-8">
          {LINK_KEYS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-${l.key}`}
              className="font-sans text-[0.82rem] font-semibold uppercase tracking-[0.14em] text-vw-ink2 hover:text-vw-green transition-colors duration-300"
            >
              {t.nav[l.key]}
            </a>
          ))}
        </div>

        <div className="flex items-center gap-5">
          <div className="hidden lg:block">
            <LangSwitcher testid="lang-switcher" />
          </div>
          <a
            href="#contact"
            data-testid="nav-quote-btn"
            className="btn-sweep hidden sm:inline-flex items-center gap-2 bg-vw-green text-white px-5 py-2.5 text-sm font-medium hover:text-white transition-colors duration-500"
          >
            {t.nav.quote} <ArrowUpRight size={16} />
          </a>
          <button
            data-testid="mobile-menu-btn"
            aria-label="Toggle menu"
            className="lg:hidden p-2 text-vw-green"
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="lg:hidden overflow-hidden bg-white border-b border-vw-silver"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINK_KEYS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-serif text-2xl text-vw-ink"
                >
                  {t.nav[l.key]}
                </a>
              ))}
              <div className="pt-2"><LangSwitcher testid="lang-switcher-mobile" /></div>
              <a href="#contact" onClick={() => setOpen(false)} className="mt-2 bg-vw-green text-white px-5 py-3 text-center text-sm">
                {t.nav.quote}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
